/**
 * User Input Interface - Domain layer
 * Defines the input structure for creating a User entity
 */
export interface IUserInput {
  id: string;
  email: string;
  name: string;
  picture?: string;
  emailVerified?: boolean;
}

/**
 * User entity - Domain layer
 * Represents the core user data structure
 */
export class User {
  private readonly _id: string;
  private readonly _email: string;
  private readonly _name: string;
  private readonly _picture?: string;
  private readonly _emailVerified?: boolean;

  constructor(input: IUserInput) {
    // Validation
    if (
      !input.id ||
      typeof input.id !== "string" ||
      input.id.trim().length === 0
    ) {
      throw new Error("User id is required and must be a non-empty string");
    }

    if (
      !input.email ||
      typeof input.email !== "string" ||
      input.email.trim().length === 0
    ) {
      throw new Error("User email is required and must be a non-empty string");
    }

    // Basic email validation
    const emailRegex = /^(?=([^\s@]+))\1@(?=([^\s@]+))\2\.(?=([^\s@]+))\3$/;
    if (!emailRegex.test(input.email)) {
      throw new Error("User email must be a valid email address");
    }

    if (
      !input.name ||
      typeof input.name !== "string" ||
      input.name.trim().length === 0
    ) {
      throw new Error("User name is required and must be a non-empty string");
    }

    if (
      input.emailVerified !== undefined &&
      typeof input.emailVerified !== "boolean"
    ) {
      throw new Error("User emailVerified must be a boolean if provided");
    }

    this._id = input.id.trim();
    this._email = input.email.trim();
    this._name = input.name.trim();
    this._picture = input.picture?.trim();
    this._emailVerified = input.emailVerified;
  }

  get id(): string {
    return this._id;
  }

  get email(): string {
    return this._email;
  }

  get name(): string {
    return this._name;
  }

  get picture(): string | undefined {
    return this._picture;
  }

  get emailVerified(): boolean | undefined {
    return this._emailVerified;
  }
}
